// src/components/PDF/OrganizePDF.js
import React, { useState, useRef } from 'react';
import { Upload, RotateCcw, RotateCw, Trash2, Download, Save } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { ReactSortable } from 'react-sortablejs'; // Fixed import
import toast from 'react-hot-toast';
import { pdfService } from '../../services/pdfService';
import { getDocument } from 'pdfjs-dist';
import PDFPreviewSave from './PDFPreviewSave';
import { useAuthGuard } from '../../hooks/useAuthGuard';

const OrganizePDF = ({ isLoading, setIsLoading }) => {
  const { requireAuth, AuthModal } = useAuthGuard();
  const [pdfFile, setPdfFile] = useState(null);
  const [pages, setPages] = useState([]);
  const [modifiedFile, setModifiedFile] = useState(null);
  const [showPreviewSave, setShowPreviewSave] = useState(false);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: false,
    onDrop: async (acceptedFiles) => {
      if (acceptedFiles[0]) {
        setPdfFile(acceptedFiles[0]);
        await loadPdfPages(acceptedFiles[0]);
      }
    }
  });

  const loadPdfPages = async (file) => {
    setIsLoading(true);
    try {
      const fileData = await file.arrayBuffer();
      const pdf = await getDocument(new Uint8Array(fileData)).promise;
      const pagePromises = [];

      for (let i = 1; i <= pdf.numPages; i++) {
        pagePromises.push(renderPageThumbnail(pdf, i));
      }

      const renderedPages = await Promise.all(pagePromises);
      setPages(renderedPages.map((canvas, index) => ({
        id: index + 1,
        originalIndex: index,
        rotation: 0,
        deleted: false,
        canvas,
        thumbnail: canvas.toDataURL()
      })));

    } catch (error) {
      toast.error('Error loading PDF');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const renderPageThumbnail = async (pdf, pageNumber) => {
    const page = await pdf.getPage(pageNumber);
    const scale = 0.3;
    const viewport = page.getViewport({ scale });
    
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    
    await page.render({
      canvasContext: canvas.getContext('2d'),
      viewport
    }).promise;
    
    return canvas;
  };

  const rotatePage = (pageId, degrees) => {
    setPages(pages.map(page => 
      page.id === pageId 
        ? { ...page, rotation: (page.rotation + degrees + 360) % 360 }
        : page
    ));
  };

  const deletePage = (pageId) => {
    setPages(pages.map(page => 
      page.id === pageId 
        ? { ...page, deleted: !page.deleted }
        : page
    ));
  };

  const saveChanges = async () => {
    if (!pdfFile) return;

    setIsLoading(true);
    try {
      const activePages = pages.filter(page => !page.deleted);
      const newOrder = activePages.map(page => page.originalIndex);
      const rotations = activePages.map(page => page.rotation);

      // First reorder pages
      let resultBlob = await pdfService.reorderPages(pdfFile, newOrder);
      
      // Then apply rotations if any
      if (rotations.some(r => r !== 0)) {
        resultBlob = await pdfService.rotatePDF(resultBlob, rotations);
      }

      setModifiedFile({
        blob: resultBlob,
        name: `organized_${pdfFile.name}`
      });

      toast.success('PDF organized successfully!');
      setShowPreviewSave(true);
    } catch (error) {
      toast.error('Error organizing PDF');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadFile = () => {
    requireAuth(() => {
      if (modifiedFile) {
        const url = URL.createObjectURL(modifiedFile.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = modifiedFile.name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    });
  };

  const resetForm = () => {
    setPdfFile(null);
    setPages([]);
    setModifiedFile(null);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold text-gray-800 mb-6">Organize PDF Pages</h2>
      
      {/* File Upload */}
      {!pdfFile && (
        <div
          {...getRootProps()}
          className={`
            border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
            ${isDragActive 
              ? 'border-[#232751] bg-[#232751]/5' 
              : 'border-gray-300 hover:border-gray-400'
            }
          `}
        >
          <input {...getInputProps()} />
          <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p className="text-sm font-medium text-gray-600 mb-2">
            {isDragActive ? 'Drop PDF here...' : 'Click to select or drag PDF here'}
          </p>
          <p className="text-xs text-gray-500">
            Upload a PDF to organize its pages
          </p>
        </div>
      )}

      {/* Page Thumbnails */}
      {pages.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-700">
              Pages ({pages.filter(p => !p.deleted).length}/{pages.length})
            </h3>
            <div className="flex space-x-3">
              <button
                onClick={saveChanges}
                disabled={isLoading}
                className="bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center disabled:opacity-50"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </button>
              <button
                onClick={resetForm}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Reset
              </button>
            </div>
          </div>
          
          <ReactSortable
            list={pages}
            setList={setPages}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4"
            animation={200}
            delayOnTouchStart={true}
            delay={2}
          >
            {pages.map((page) => (
              <div
                key={page.id}
                className={`
                  relative bg-white rounded-lg shadow-md overflow-hidden cursor-move transition-all
                  ${page.deleted ? 'opacity-50 grayscale' : 'hover:shadow-lg'}
                `}
                style={{
                  transform: `rotate(${page.rotation}deg)`
                }}
              >
                <img
                  src={page.thumbnail}
                  alt={`Page ${page.id}`}
                  className="w-full h-auto"
                  draggable={false}
                />
                
                {/* Controls */}
                <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center opacity-0 hover:opacity-100">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => rotatePage(page.id, -90)}
                      className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
                      title="Rotate Left"
                    >
                      <RotateCcw className="w-4 h-4 text-gray-700" />
                    </button>
                    <button
                      onClick={() => rotatePage(page.id, 90)}
                      className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
                      title="Rotate Right"
                    >
                      <RotateCw className="w-4 h-4 text-gray-700" />
                    </button>
                    <button
                      onClick={() => deletePage(page.id)}
                      className={`p-2 rounded-full shadow-lg transition-colors ${
                        page.deleted 
                          ? 'bg-green-500 hover:bg-green-600 text-white' 
                          : 'bg-red-500 hover:bg-red-600 text-white'
                      }`}
                      title={page.deleted ? "Restore Page" : "Delete Page"}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {/* Page Number */}
                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-center py-1 text-sm">
                  Page {page.id}
                </div>
              </div>
            ))}
          </ReactSortable>
        </div>
      )}

      {/* Download Result */}
      {modifiedFile && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-green-800">PDF organized successfully!</p>
              <p className="text-sm text-green-600">Your organized PDF is ready for download</p>
            </div>
            <button
              onClick={downloadFile}
              className="bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center"
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </button>
          </div>
        </div>
      )}

      {AuthModal}

      <PDFPreviewSave
        isOpen={showPreviewSave}
        onClose={() => setShowPreviewSave(false)}
        pdfBlob={modifiedFile?.blob}
        fileName={modifiedFile?.name}
        operationType="organized"
        onSaveComplete={() => toast.success('Organized PDF saved to your library!')}
      />
    </div>
  );
};

export default OrganizePDF;
